"use client";

import { AreaChart, Area } from "@/components/charts/area-chart";
import { Grid } from "@/components/charts/grid";
import { XAxis } from "@/components/charts/x-axis";
import { YAxis } from "@/components/charts/y-axis";
import { ChartTooltip } from "@/components/charts/tooltip";
import { ReferenceArea } from "@/components/charts/reference-area";
import { ReferenceAreaLabel } from "@/components/charts/reference-area-label";
import { resolveCurve, type CurveName } from "@/components/charts/curve-map";
import { ChartBrush, ChartBrushLayout } from "@/components/charts/chart-brush";
import type { ChartBrushLayoutRenderProps } from "@/components/charts/chart-brush";
import { SwipeScroll } from "@/components/charts/swipe-scroll";
import {
  ChartAxisFrame,
  ChartXAxisRow,
} from "@/components/charts/chart-axis-frame";
import { ChartLegendHoverProvider } from "@/components/charts/chart-legend-hover";
import { useLegendClickSync } from "@/components/charts/use-legend-click-sync";
import {
  Legend,
  LegendItem,
  LegendLabel,
  LegendMarker,
} from "@/components/charts/legend";
import { cn } from "@/lib/utils";
import {
  ChartDataPoint,
  ChartThreshold,
  type ModuleChartSeriesMeta,
  type ModuleReferenceArea,
} from "@/types/modules";
import { stripInlineMarkdown } from "@/utils/inline-markdown";
import React, { JSX, useMemo, useState } from "react";
import {
  moduleAxisLabels,
} from "./chart-module-metrics";
import { CHART_PLOT_HEIGHT, MIN_CHART_WIDTH, MIN_PX_PER_POINT } from "@/config/layout";
import { accentChartColors } from "../module-accent";
import { chartPaletteStyle } from "../imports-token-scope";

const DEFAULT_DATA: ChartDataPoint[] = [
  { name: "Page A", uv: 4000, pv: 2400 },
  { name: "Page B", uv: 3000, pv: 1398 },
  { name: "Page C", uv: 2000, pv: 9800 },
  { name: "Page D", uv: 2780, pv: 3908 },
  { name: "Page E", uv: 1890, pv: 4800 },
  { name: "Page F", uv: 2390, pv: 3800 },
  { name: "Page G", uv: 3490, pv: 4300 },
];

const DEFAULT_SERIES: ModuleChartSeriesMeta[] = [
  { key: "uv", label: "Unique visitors" },
  { key: "pv", label: "Page views" },
];

const CHART_HEIGHT = CHART_PLOT_HEIGHT.area;
const PX_PER_POINT = MIN_PX_PER_POINT.area;

export default function ModuleAreaChart({
  data = DEFAULT_DATA,
  xAxisKey = "name",
  xAxisLabel,
  yAxisLabel,
  thresholds,
  series: seriesProp,
  showBrush = false,
  showGrid = true,
  showMarkers = false,
  showLegend,
  fadeEdges = true,
  curve,
  referenceArea,
  grayscale,
  accent,
  className,
}: {
  data?: ChartDataPoint[];
  xAxisKey?: string;
  xAxisLabel?: string;
  yAxisLabel?: string;
  thresholds?: ChartThreshold[];
  series?: ModuleChartSeriesMeta[];
  showBrush?: boolean;
  showGrid?: boolean;
  showMarkers?: boolean;
  showLegend?: boolean;
  fadeEdges?: boolean;
  curve?: CurveName;
  referenceArea?: ModuleReferenceArea;
  grayscale?: boolean;
  accent?: number;
  className?: string;
}): JSX.Element {
  const series = seriesProp ?? (data === DEFAULT_DATA ? DEFAULT_SERIES : undefined);
  const palette = accentChartColors(grayscale, accent);
  const dataKeys = React.useMemo(() => {
    if (series && series.length > 0) return series.map((s) => s.key);
    if (!data || data.length === 0) return [];
    const first = data[0];
    if (!first || typeof first !== "object") return [];
    return Object.keys(first).filter((key) => key !== xAxisKey);
  }, [data, xAxisKey, series]);

  const seriesColor = (key: string, index: number) => {
    const meta = series?.find((s) => s.key === key);
    return meta?.color ?? palette[index % palette.length] ?? "#000";
  };
  const seriesLabel = (key: string) => {
    const meta = series?.find((s) => s.key === key);
    return stripInlineMarkdown(meta?.label) || key;
  };

  const chartDataFull = useMemo(
    () =>
      data.map((d) => ({
        ...d,
        [xAxisKey]: stripInlineMarkdown(String(d[xAxisKey] ?? "")),
      })),
    [data, xAxisKey],
  );

  const yThresholds = useMemo(
    () => (thresholds ?? []).filter((t) => t.axis !== "x"),
    [thresholds],
  );

  const legendVisible = showLegend ?? dataKeys.length > 1;

  const [legendHovered, setLegendHovered] = useState<number | null>(null);
  const {
    activeIndex,
    handleSelect,
    setLabelRef,
    selectedOrigin,
    legendSelectedIndex,
    elementIndex,
    toggleElementIndex,
  } = useLegendClickSync();

  return (
    <ChartLegendHoverProvider
      hoveredIndex={legendHovered}
      onHoverChange={setLegendHovered}
      selectedIndex={activeIndex}
      selectedOrigin={selectedOrigin}
      elementIndex={elementIndex}
      onElementToggle={toggleElementIndex}
    >
    <ChartBrushLayout
      data={chartDataFull as unknown as Record<string, unknown>[]}
      xDataKey={xAxisKey}
    >
      {(layout: ChartBrushLayoutRenderProps<Record<string, unknown>>) => {
        const visibleData = layout.filteredData;
        return (
    <div className={cn("flex w-full flex-col gap-3", className)} style={chartPaletteStyle(grayscale, accent)}>
      <ChartAxisFrame {...moduleAxisLabels({ xAxisLabel, yAxisLabel })}>
      <SwipeScroll className="min-w-0 flex-1 pb-1 [mask-image:linear-gradient(to_right,black_0%,black_95%,transparent_100%)] [-webkit-mask-image:linear-gradient(to_right,black_0%,black_95%,transparent_100%)]">
      <div
        style={{
          minWidth: Math.max(MIN_CHART_WIDTH, visibleData.length * PX_PER_POINT),
        }}
      >
      <div style={{ height: CHART_HEIGHT }}>
        <AreaChart
          data={visibleData}
          xDataKey={xAxisKey}
          xScaleKind="band"
          margin={{ top: 10, right: 24, bottom: 8, left: 0 }}
          aspectRatio="auto"
          className="h-full"
        >
          {showGrid && (
            <Grid
              horizontal
              vertical={false}
              highlightRowValues={
                yThresholds.length > 0
                  ? yThresholds.map((t) => t.value)
                  : undefined
              }
              highlightRowStroke={yThresholds[0]?.color ?? (grayscale ? "var(--chart-5)" : "var(--destructive)")}
              highlightRowStrokeDasharray="4,4"
              highlightRowStrokeWidth={1.5}
            />
          )}
          {referenceArea && (
            <>
              <ReferenceArea
                y1={referenceArea.y1}
                y2={referenceArea.y2}
                fill={referenceArea.color ?? "var(--chart-1)"}
                fillOpacity={0.15}
                stroke={referenceArea.color ?? "var(--chart-1)"}
                strokeStyle="dashed"
                strokeWidth={1.5}
                fadeEdges
                fadeEdgesLength={12}
                pattern={
                  referenceArea.variant === "pattern"
                    ? (referenceArea.pattern ?? "diagonal")
                    : "none"
                }
              />
              {referenceArea.label && (
                <ReferenceAreaLabel
                  y2={referenceArea.y2}
                  label={stripInlineMarkdown(referenceArea.label)}
                />
              )}
            </>
          )}
          <YAxis />
          {dataKeys.map((key, i) => {
            const color = seriesColor(key, i);
            return (
              <Area
                key={key}
                dataKey={key}
                stroke={color}
                strokeWidth={2}
                fill={color}
                fillOpacity={0.25}
                gradientToOpacity={0}
                fadeEdges={fadeEdges}
                showHighlight={showMarkers}
                curve={resolveCurve(curve)}
              />
            );
          })}
          <XAxis />
          <ChartTooltip
            showDatePill
            rows={(point) =>
              dataKeys.map((key, i) => ({
                color: seriesColor(key, i),
                label: seriesLabel(key),
                value: Number(point[key] ?? 0),
              }))
            }
          />
        </AreaChart>
      </div>
      <ChartXAxisRow />
      </div>
      </SwipeScroll>
      </ChartAxisFrame>

      {showBrush && chartDataFull.length > 0 && (
        <ChartBrush
          data={chartDataFull as unknown as Record<string, unknown>[]}
          xDataKey={xAxisKey}
          variant="band"
          height={36}
          onSelectionChange={layout.onBrushSelectionChange}
          initialSelection={layout.brushSelection ?? undefined}
          previewSeries={dataKeys.map((key, i) => ({
            dataKey: key,
            color: seriesColor(key, i),
          }))}
        />
      )}

      {legendVisible && (
        <SwipeScroll className="w-full pb-1" fade="auto">
          <Legend
            items={dataKeys.map((key, i) => ({
              label: seriesLabel(key),
              value: data?.length ?? 0,
              color: seriesColor(key, i),
            }))}
            hoveredIndex={legendHovered}
            onHoverChange={setLegendHovered}
            selectedIndex={legendSelectedIndex}
            onSelect={handleSelect}
            onLabelRef={setLabelRef}
            className="flex flex-row gap-6"
          >
            <LegendItem className="flex shrink-0 items-center gap-2">
              <LegendMarker className="h-2.5 w-2.5" />
              <LegendLabel />
            </LegendItem>
          </Legend>
        </SwipeScroll>
      )}
    </div>
        );
      }}
    </ChartBrushLayout>
    </ChartLegendHoverProvider>
  );
}
